import React, { useEffect, useState, useRef } from "react";
import CustomInput from "../components/CustomInput";
import { Link, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { login } from "../features/auth/authSlice";
import { useTheme } from "../contexts/ThemeContext";
import { IoMdSunny, IoMdMoon } from "react-icons/io";

// Enhanced validation schema with better security
let schema = yup.object().shape({
  email: yup
    .string()
    .email("Email should be valid")
    .required("Email is Required")
    .max(100, "Email cannot exceed 100 characters")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Please enter a valid email address"
    ),
  password: yup
    .string()
    .required("Password is Required")
    .min(8, "Password must be at least 8 characters")
    .max(128, "Password cannot exceed 128 characters")
    .matches(
      /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      "Password must contain at least one letter, one number and one special character"
    ),
});

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { darkMode, toggleTheme } = useTheme();
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      // Sanitize input
      const sanitizedValues = {
        email: values.email.trim().toLowerCase(),
        password: values.password
      };
      
      // Execute reCAPTCHA
      if (window.grecaptcha && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
        try {
          const token = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, { action: 'admin_login' });
          setRecaptchaToken(token);
          sanitizedValues.recaptchaToken = token;
        } catch (error) {
          console.error('reCAPTCHA execution error:', error);
          // Still proceed with login even if reCAPTCHA fails (better UX)
        }
      }
      
      dispatch(login(sanitizedValues));
    },
  });
  
  const authState = useSelector((state) => state);
  const { user, isError, isSuccess, isLoading, message } = authState.auth;

  useEffect(() => {
    if (isSuccess) {
      window.location.href = "/admin";
    } else {
      navigate("");
    }
    
    // Load reCAPTCHA script
    const loadRecaptchaScript = () => {
      if (!document.getElementById('recaptcha-script')) {
        const script = document.createElement('script');
        script.id = 'recaptcha-script';
        script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
      }
    };
    
    loadRecaptchaScript();
  }, [user, isError, isSuccess, isLoading, navigate]);
  
  return (
    <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-yellow-300'}`}>
      <div className="absolute top-4 right-4">
        <button 
          className="theme-toggle p-2 rounded-full shadow-lg bg-white dark:bg-gray-700"
          onClick={toggleTheme}
          aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
        >
          {darkMode ? (
            <IoMdSunny size={24} className="text-yellow-400" />
          ) : (
            <IoMdMoon size={24} className="text-gray-700" />
          )}
        </button>
      </div>
      
      <div className={`w-full max-w-md rounded-lg shadow-lg p-6 md:p-8 m-4 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
        <h3 className="text-xl md:text-2xl font-bold text-center mb-4 md:mb-6">Admin Login</h3>
        <p className="text-center mb-4 md:mb-6 text-sm md:text-base">Login to your admin account to continue.</p>
        
        <div className="error text-center text-red-500 mb-4 text-sm">
          {message.message == "Rejected" ? "You are not an Admin" : ""}
          {isError ? "Invalid credentials. Please try again." : ""}
          {message?.response?.data?.message ? message.response.data.message : ""}
        </div>
        
        <form onSubmit={formik.handleSubmit}>
          <div className="mb-3 md:mb-4">
            <CustomInput
              type="text"
              label="Email Address"
              id="email"
              name="email"
              onChng={formik.handleChange("email")}
              onBlr={formik.handleBlur("email")}
              val={formik.values.email}
            />
            <div className="error mt-1 text-red-500 text-xs md:text-sm">
              {formik.touched.email && formik.errors.email}
            </div>
          </div>
          
          <div className="mb-4 md:mb-6">
            <CustomInput
              type="password"
              label="Password"
              id="pass"
              name="password"
              onChng={formik.handleChange("password")}
              onBlr={formik.handleBlur("password")}
              val={formik.values.password}
            />
            <div className="error mt-1 text-red-500 text-xs md:text-sm">
              {formik.touched.password && formik.errors.password}
            </div>
          </div>
          
          <button
            className={`w-full py-2 md:py-3 px-4 rounded font-bold text-white transition duration-300 ${
              darkMode 
                ? 'bg-blue-600 hover:bg-blue-700' 
                : 'bg-yellow-500 hover:bg-yellow-600'
            }`}
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;